import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { SiteFooter } from './SiteFooter';
import { useAuth } from '../contexts/AuthContext';
import { BrandLogo } from './BrandLogo';

const API_URL = process.env.REACT_APP_API_URL || '';

export const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  /**
   * Why the user is here, when they did not choose to be. The 401 interceptor in AuthContext
   * redirects to /login?expired=1; reading it from the URL rather than storage means several
   * simultaneous 401s cannot race each other into losing the flag.
   */
  const expired = new URLSearchParams(window.location.search).get('expired') === '1';
  const { login } = useAuth();

  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSending, setResetSending] = useState(false);
  // The confirmation is shown regardless of whether the address has an account —
  // telling the user "no such account" would let anyone probe for valid addresses.
  const [resetSent, setResetSent] = useState(false);

  const handleRequestReset = async () => {
    setResetSending(true);
    try {
      await fetch(`${API_URL}/api/auth/request-password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail }),
      });
    } catch (err) {
      // Deliberately ignored: a network failure must not reveal more than a success
    } finally {
      setResetSending(false);
      setResetSent(true);
    }
  };

  const closeReset = () => {
    setResetOpen(false);
    setResetSent(false);
    setResetEmail('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    const success = await login(email, password);
    if (!success) {
      setError('Invalid credentials');
    }
  };

  return (
    <>
      {/*
        Sits where a header bar would be, without introducing one. The signed-out pages have no
        AppBar — adding a whole header for a single link would be more chrome than the page
        needs — so this floats at the top right of the viewport instead.

        Fixed rather than absolute so it holds its place on a short screen, and deliberately
        outside the Container: the sign-in column is only 600px wide, so a link inside it would
        sit well short of the screen edge and read as part of the form rather than as page
        navigation. This is the only route to /about for a signed-out visitor.
      */}
      <Box sx={{ position: 'fixed', top: 16, right: 24, zIndex: 1 }}>
        <Link component={RouterLink} to="/about" variant="body2" underline="hover">
          About
        </Link>
      </Box>

    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box sx={{ mb: 3 }}>
          <BrandLogo maxWidth={300} height={56} />
        </Box>
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            Social Media Manager
          </Typography>
          <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
            Sign in to manage your social media posts
          </Typography>
          {expired && !error && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Your session ended, so you have been signed out. Sign in again to carry on —
              nothing was lost.
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
            >
              Sign In
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => { setResetEmail(email); setResetOpen(true); }}
              >
                Forgot your password?
              </Link>
            </Box>

            <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 2 }}>
              By signing in, you agree to our{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                Privacy Policy
              </a>
            </Typography>
          </Box>
        </Paper>

      </Box>

      <Dialog open={resetOpen} onClose={closeReset} maxWidth="xs" fullWidth>
        <DialogTitle>{resetSent ? 'Request sent' : 'Reset your password'}</DialogTitle>
        <DialogContent>
          {resetSent ? (
            <DialogContentText>
              If an account exists for that address, an administrator has been notified and
              will reset the password for you. Get in touch with them if you do not hear back.
            </DialogContentText>
          ) : (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                Enter your email address and an administrator will be asked to reset your
                password. The reset is done by a person, not automatically — they are emailed
                the request.
              </DialogContentText>
              <TextField
                autoFocus
                fullWidth
                size="small"
                label="Email address"
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && resetEmail.trim()) handleRequestReset();
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          {resetSent ? (
            <Button onClick={closeReset} variant="contained">Close</Button>
          ) : (
            <>
              <Button onClick={closeReset}>Cancel</Button>
              <Button
                onClick={handleRequestReset}
                variant="contained"
                disabled={resetSending || !resetEmail.trim()}
              >
                {resetSending ? 'Sending…' : 'Request reset'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <SiteFooter variant="standalone" />
    </Container>
    </>
  );
};
